import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import GetimgAILandingHelmet from 'common/helmets/GetimgAILandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import Getimgai1 from 'img/landing/getimgai-1.webp';
import Getimgai2 from 'img/landing/getimgai-2.webp';
import Getimgai3 from 'img/landing/getimgai-3.webp';
import Wojciech from 'img/landing/wojciech.webp';

import GetimgWordmark from 'img/landing/getimg-wordmark.svg';

import 'css/components/www/_LandingCustomersPage.scss';

const GetimgAILanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Appcues Case Study');
  }, [trackEvent]);

  return (
    <div className="landingGetimgAI landingCustomersPage">
      <GetimgAILandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How getimg.ai prioritizes feature development using Canny's feedback boards and user voting system" />
          <div className="headerImage">
            <div className="headerImage">
              <div className="nametag">
                <div className="name">Wojciech Krzciński</div>
                <div className="title">Founder&apos;s Associate at getimg.ai</div>
              </div>
              <LazyLoadedImage loading="eager" src={Wojciech} alt="Wojciech Krzciński" />
            </div>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p className="quote">
              "Our users appreciate providing feedback through Canny, often sharing that they feel
              more heard and valued. We&apos;ve seen hundreds of posts and thousands of votes since
              we&apos;ve implemented Canny."
            </p>
            <p>
              As an all-in-one AI creative toolkit, getimg.ai is on a mission to enable anyone to
              bring their imaginative ideas to life&mdash;regardless of their technical background
              or skills.
            </p>
            <p>
              Wojciech Krzciński is the Founder&apos;s Associate at getimg.ai, hence he wears many
              hats. He&apos;s hands-on with customer support, and he collaborates with the
              development and finance departments to drive product growth.
            </p>
            <p>
              getimg.ai has millions of registered users and they process over 1 million images
              daily.
            </p>
            <h2 className="sectionTitle">Problem</h2>
            <p>
              With such a huge user base, they get a high volume of customer feedback coming through
              daily from various channels—especially on Discord, through email, and on Twitter.
            </p>
            <p className="quote">
              "To consolidate this input, we initially created and updated spreadsheets regularly.
              However, this process was overwhelming and didn&apos;t effectively highlight the most
              popular feature requests."
            </p>
            <p>
              In order to prioritize feedback, Wojciech recognized the need for a voting system.
              With a high volume of feedback coming in, he also needed a system to organize it, as
              well as identify and manage duplicates.
            </p>
            <p className="quote">
              "We realized it was time to find a more efficient solution to address this challenge."
            </p>

            <h2 className="sectionTitle">Solution</h2>
            <p>
              Wojciech set up a feedback board in Canny, and started directing his users to it to
              leave their feature requests. Users can access it from the links in the getimg.ai app
              or Discord.
            </p>

            <p className="quote">
              "Users started voting on existing ideas, giving us clear insights into their needs.
              This has become a valuable tool for prioritizing feature development."
            </p>

            <LazyLoadedImage className="border" src={Getimgai1} alt="" />

            <p>
              Thanks to the feedback collected through Canny, Wojciech&apos;s team prioritized
              building an Image to Prompt feature.
            </p>

            <LazyLoadedImage className="border" src={Getimgai2} alt="" />

            <p>
              This feature allows users to simply upload an image, and AI will describe it for them.
              As a result, they get a prompt that enables them to generate a similar image to the
              uploaded one.
            </p>

            <p>
              They also began using Canny for their changelog, where they create detailed release
              notes for product announcements. This lets interested users know when requested
              features ship.
            </p>

            <p className="quote">
              "Thanks to this, our customers are informed about new additions to our software, and
              new users can see that we are still developing our software - there is a history of
              regular changes."
            </p>

            <LazyLoadedImage className="border" src={Getimgai3} alt="" />

            <p>
              The changelog is also helpful for their own team to reference and track recent
              changes, and see when they occurred.
            </p>

            <h2 className="sectionTitle">Results</h2>
            <p>
              Canny helps getimg.ai save their team a significant amount of time managing user
              feedback.{' '}
            </p>
            <p>
              Before Canny, they were spending about eight hours per month tracking feedback in
              spreadsheets.
            </p>

            <p className="quote">
              "If you&apos;re considering Canny, I highly recommend giving it a try - you&apos;ll be
              glad you did and won&apos;t look back. It&apos;s particularly beneficial for SaaS
              companies like ours."
            </p>

            <p>
              <a href="https://canny.io/features/collect-feedback" rel="noopener" target="_blank">
                Create your own feedback board
              </a>{' '}
              to understand what your customers want to see next. You&apos;ll save time, stay
              organized, and better engage users.
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage alt="getimg.ai Company" className="companyLogo" src={GetimgWordmark} />
            getimg.ai is one of the leading platforms for AI-based image creation and editing.
            <a href="https://getimg.ai" rel="noopener" target="_blank">
              getimg.ai
            </a>
            <div className="stat">
              <UppercaseHeader>Years on Canny</UppercaseHeader>
              <p>1</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>2,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default hot(GetimgAILanding);
